/**
 * Cliente HTTP contra el backend de TORA.
 *
 * El frontend NUNCA habla con la base: habla con el Hono de `localhost:3001`,
 * que es donde viven la autenticacion y las reglas de negocio. Este modulo es la
 * unica puerta: si manana la URL cambia o el sobre de respuesta cambia, se toca
 * aca y nada mas.
 *
 * TRES COSAS QUE NO SON OPCIONALES
 * --------------------------------
 * 1. `credentials: "include"`. La sesion viaja en una cookie httpOnly que el
 *    JavaScript no puede leer ni por accidente. Sin esta linea el navegador no
 *    la manda en cross-origin (5174 -> 3001) y todo responde 401.
 *
 * 2. El mensaje del backend llega INTACTO a la UI. Los RPCs de la base usan
 *    `raise exception` con textos escritos para que los lea un vendedor:
 *    "La venta excede el credito disponible del cliente (queda $ 500.000).
 *    Confirma la venta con forzar si igual queres hacerla". Reemplazar eso por
 *    "Error 422" convierte una instruccion en un misterio.
 *
 * 3. Un 401 tiene que ser distinguible de cualquier otra falla. Es la unica
 *    condicion que se resuelve mandando a la persona al login, y confundirlo con
 *    un error de red produce una app que se queda en blanco sin explicar nada.
 */

/**
 * Base del backend. Relativa a proposito.
 *
 * En produccion el backend sirve estas mismas pantallas, asi que `/api` apunta
 * al mismo origen: las cookies de sesion (httpOnly + SameSite) viajan sin
 * excepciones de CORS. En desarrollo, Vite proxea `/api` al backend en :3001,
 * con lo cual el navegador tampoco ve dos origenes distintos.
 *
 * `VITE_API_URL` sigue disponible para apuntar a un backend remoto.
 */
export const URL_API: string =
  (import.meta.env?.VITE_API_URL as string | undefined)?.replace(/\/+$/, "") ?? "/api";

/**
 * Cuanto se espera antes de cortar. Quince segundos es mucho para un local con
 * el backend en la misma maquina y poco para un import de lista pesada; las
 * llamadas largas pasan su propio `timeout`.
 */
export const TIMEOUT_MS = 15_000;

// ─────────────────────────────────────────────────────────────────────────────
// El error
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Una falla de la API con lo que la UI necesita para reaccionar.
 *
 * `mensaje` es el texto del backend tal cual vino. `message` (el de `Error`)
 * lleva lo mismo, para que un `console.error` o un ErrorBoundary muestren algo
 * util sin conocer esta clase.
 */
export class ErrorApi extends Error {
  readonly mensaje: string;
  readonly codigo: string;
  readonly estado: number;
  /** Errores por campo, cuando el backend valido con Zod y los mando. */
  readonly detalles: Record<string, string[]>;
  /** El cuerpo crudo, por si una pantalla necesita algo que aca no se modelo. */
  readonly cuerpo: unknown;

  constructor(params: {
    mensaje: string;
    codigo?: string;
    estado?: number;
    detalles?: Record<string, string[]>;
    cuerpo?: unknown;
  }) {
    super(params.mensaje);
    this.name = "ErrorApi";
    this.mensaje = params.mensaje;
    this.codigo = params.codigo ?? "DESCONOCIDO";
    this.estado = params.estado ?? 0;
    this.detalles = params.detalles ?? {};
    this.cuerpo = params.cuerpo;
    // `extends Error` con target ES2022 ya deja bien el prototipo, pero el
    // downlevel de algunos bundlers no: sin esto `instanceof` falla.
    Object.setPrototypeOf(this, ErrorApi.prototype);
  }

  /** El caso que manda al login. Ver `esNoAutenticado`. */
  get noAutenticado(): boolean {
    return this.estado === 401;
  }

  /** Sesion valida pero sin permiso: un operador tocando algo de admin. */
  get sinPermiso(): boolean {
    return this.estado === 403;
  }

  /** No llego a haber respuesta: red caida, backend apagado, timeout. */
  get esDeRed(): boolean {
    return this.estado === 0;
  }
}

/**
 * Un 401 y nada mas que un 401.
 *
 * Se exporta como funcion suelta porque el `catch` de una mutacion recibe
 * `unknown` y comparar `estado === 401` a mano en cada pantalla es la clase de
 * repeticion que termina mal en un solo lugar.
 */
export function esNoAutenticado(error: unknown): boolean {
  return error instanceof ErrorApi && error.noAutenticado;
}

/** El texto que hay que mostrarle a la persona, venga de donde venga el error. */
export function mensajeDeError(error: unknown): string {
  if (error instanceof ErrorApi) return error.mensaje;
  if (error instanceof Error && error.message) return error.message;
  return "Paso algo que no supimos interpretar. Volve a intentar.";
}

// ─────────────────────────────────────────────────────────────────────────────
// Aviso de sesion caida
// ─────────────────────────────────────────────────────────────────────────────

type Escucha = () => void;
const escuchasSesion = new Set<Escucha>();

/**
 * Avisa cuando el backend contesto 401.
 *
 * La cookie de sesion dura 24 horas y vence sola: la persona deja la pantalla
 * abierta, vuelve al otro dia y el primer click responde 401. Sin este aviso, la
 * app se queda mostrando datos viejos y cada accion falla en silencio.
 */
export function alPerderSesion(escucha: Escucha): () => void {
  escuchasSesion.add(escucha);
  return () => escuchasSesion.delete(escucha);
}

function avisarSesionPerdida(): void {
  for (const escucha of escuchasSesion) {
    try {
      escucha();
    } catch {
      // Un listener roto no puede impedir que los otros se enteren.
    }
  }
}

/**
 * Avisa cuando el backend contesto que el sistema esta en pausa (codigo PAUSA).
 * El shell tapa la pantalla con un aviso hasta que se recargue.
 */
const escuchasPausa = new Set<Escucha>();

export function alPausar(escucha: Escucha): () => void {
  escuchasPausa.add(escucha);
  return () => escuchasPausa.delete(escucha);
}

function avisarPausa(): void {
  for (const escucha of escuchasPausa) {
    try {
      escucha();
    } catch {
      // Igual que con la sesion: un listener roto no frena a los otros.
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// El sobre
// ─────────────────────────────────────────────────────────────────────────────

interface Sobre {
  ok: boolean;
  data: unknown;
  error: string | null;
  codigo?: string;
}

/**
 * El backend contesta de DOS formas y las dos son validas.
 *
 *   · `/health` y el handler global de errores usan el sobre completo:
 *     `{ ok, data, error }`.
 *   · Las rutas de negocio devuelven el objeto directo: `{ cajas: [...] }`,
 *     `{ filas, total }`, la fila del producto recien creado.
 *
 * Unificar eso en el backend seria mejor, pero el backend no es de este proceso
 * y una respuesta desenvuelta de mas ("data" como nombre de campo legitimo)
 * romperia una pantalla. La deteccion exige las tres claves del sobre.
 */
function esSobre(cuerpo: unknown): cuerpo is Sobre {
  return (
    typeof cuerpo === "object" &&
    cuerpo !== null &&
    "ok" in cuerpo &&
    typeof (cuerpo as Sobre).ok === "boolean" &&
    "data" in cuerpo &&
    "error" in cuerpo
  );
}

/** Desenvuelve el sobre si esta; devuelve el cuerpo tal cual si no. */
export function desenvolver<T>(cuerpo: unknown): T {
  return (esSobre(cuerpo) ? cuerpo.data : cuerpo) as T;
}

// ─────────────────────────────────────────────────────────────────────────────
// Lectura del error
// ─────────────────────────────────────────────────────────────────────────────

function texto(v: unknown): string | null {
  return typeof v === "string" && v.trim() !== "" ? v.trim() : null;
}

/**
 * Junta los mensajes por campo de una validacion Zod.
 *
 * `datosInvalidos()` del backend contesta `error: "Datos invalidos"` y guarda lo
 * que de verdad pasa en `detalles` y `generales`: "Un pago con cheque tiene que
 * traer los datos del cheque", "Si cargas costo tenes que decir costo_moneda".
 * Mostrar solo "Datos invalidos" tira a la basura la unica parte util.
 */
function leerDetalles(cuerpo: Record<string, unknown>): {
  detalles: Record<string, string[]>;
  frases: string[];
} {
  const detalles: Record<string, string[]> = {};
  const frases: string[] = [];

  const crudo = cuerpo.detalles;
  if (typeof crudo === "object" && crudo !== null) {
    for (const [campo, valor] of Object.entries(crudo as Record<string, unknown>)) {
      const lista = (Array.isArray(valor) ? valor : [valor])
        .map(texto)
        .filter((v): v is string => v !== null);
      if (lista.length > 0) {
        detalles[campo] = lista;
        frases.push(...lista);
      }
    }
  }

  // `generales` son los `.refine` de nivel objeto: no cuelgan de ningun campo y
  // si no se leen aca se pierden.
  if (Array.isArray(cuerpo.generales)) {
    for (const v of cuerpo.generales) {
      const t = texto(v);
      if (t) frases.push(t);
    }
  }

  return { detalles, frases };
}

const GENERICOS = new Set(["datos invalidos", "los datos recibidos no son validos."]);

function errorDeRespuesta(estado: number, cuerpo: unknown): ErrorApi {
  // Respuesta no-JSON (un 502 del proxy, un HTML de error): no hay nada que leer.
  if (typeof cuerpo !== "object" || cuerpo === null) {
    return new ErrorApi({
      mensaje: texto(cuerpo) ?? `El servidor respondio ${estado} sin explicacion.`,
      codigo: "RESPUESTA_INESPERADA",
      estado,
      cuerpo,
    });
  }

  const obj = cuerpo as Record<string, unknown>;
  const { detalles, frases } = leerDetalles(obj);

  // `error` viene como string en las dos formas: sobre y objeto directo.
  let mensaje = texto(obj.error) ?? texto(obj.mensaje);

  // Si el `error` es el rotulo generico de validacion, mandan los detalles.
  if (frases.length > 0 && (mensaje === null || GENERICOS.has(mensaje.toLowerCase()))) {
    mensaje = frases.join(" ");
  }

  return new ErrorApi({
    mensaje: mensaje ?? mensajePorDefecto(estado),
    codigo: texto(obj.codigo) ?? codigoPorDefecto(estado),
    estado,
    detalles,
    cuerpo,
  });
}

function mensajePorDefecto(estado: number): string {
  if (estado === 401) return "Tu sesion venció. Volvé a entrar.";
  if (estado === 403) return "Tu usuario no tiene permiso para hacer esto.";
  if (estado === 404) return "No encontramos lo que buscabas.";
  if (estado >= 500) return "El servidor tuvo un problema. Probá de nuevo en un momento.";
  return `El servidor respondio ${estado}.`;
}

function codigoPorDefecto(estado: number): string {
  if (estado === 401) return "NO_AUTENTICADO";
  if (estado === 403) return "SIN_PERMISO";
  if (estado === 404) return "NO_ENCONTRADO";
  return "ERROR_HTTP";
}

// ─────────────────────────────────────────────────────────────────────────────
// La llamada
// ─────────────────────────────────────────────────────────────────────────────

export type Metodo = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

export interface OpcionesPedido {
  /** Query string. Los `undefined` y `null` no se mandan. */
  params?: Record<string, string | number | boolean | undefined | null>;
  /** Cuerpo JSON. */
  cuerpo?: unknown;
  /**
   * Cuerpo multipart, para subir un archivo. Excluyente con `cuerpo`.
   *
   * El `Content-Type` NO se pone a mano: el navegador lo arma con el `boundary`
   * que separa las partes, y escribirlo nosotros lo deja sin boundary y el
   * backend no puede parsear nada.
   */
  formulario?: FormData;
  /** Milisegundos antes de abortar. Por defecto `TIMEOUT_MS`. */
  timeout?: number;
  /** Cancelacion externa (TanStack Query pasa la suya). */
  signal?: AbortSignal;
}

function armarUrl(ruta: string, params?: OpcionesPedido["params"]): string {
  const camino = ruta.startsWith("/") ? ruta : `/${ruta}`;
  if (!params) return `${URL_API}${camino}`;

  const qs = new URLSearchParams();
  for (const [clave, valor] of Object.entries(params)) {
    if (valor === undefined || valor === null || valor === "") continue;
    qs.set(clave, String(valor));
  }
  const texto = qs.toString();
  return texto === "" ? `${URL_API}${camino}` : `${URL_API}${camino}?${texto}`;
}

/**
 * Combina el timeout con la cancelacion del caller.
 *
 * `AbortSignal.timeout` y `AbortSignal.any` existen en todos los navegadores que
 * corren esta app, pero no siempre en el entorno de test. El guard evita que un
 * runtime viejo tire "is not a function" y se lleve puesto todo el cliente.
 */
function armarSignal(timeout: number, externa?: AbortSignal): AbortSignal | undefined {
  const conTimeout =
    typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout === "function"
      ? AbortSignal.timeout(timeout)
      : undefined;

  if (!externa) return conTimeout;
  if (!conTimeout) return externa;

  if (typeof AbortSignal.any === "function") return AbortSignal.any([externa, conTimeout]);
  return externa;
}

/**
 * Hace el pedido, desenvuelve, y tira `ErrorApi` si algo salio mal.
 *
 * Lo que sale de aca ya es el dato que la pantalla necesita: sin sobre, sin
 * status, sin `response.ok` para chequear.
 */
export async function pedir<T = unknown>(
  metodo: Metodo,
  ruta: string,
  opciones: OpcionesPedido = {},
): Promise<T> {
  const { params, cuerpo, formulario, timeout = TIMEOUT_MS, signal } = opciones;

  let respuesta: Response;
  try {
    respuesta = await fetch(armarUrl(ruta, params), {
      method: metodo,
      // La sesion es una cookie httpOnly y el backend esta en otro puerto: sin
      // esto el navegador no la manda y todo contesta 401.
      credentials: "include",
      headers:
        formulario !== undefined || cuerpo === undefined
          ? undefined
          : { "Content-Type": "application/json" },
      body: formulario ?? (cuerpo === undefined ? undefined : JSON.stringify(cuerpo)),
      signal: armarSignal(timeout, signal),
    });
  } catch (error) {
    // Una cancelacion pedida por el caller se propaga tal cual: TanStack Query
    // la usa para descartar una consulta que quedo obsoleta, y convertirla en
    // ErrorApi haria aparecer un toast por cada tecla que alguien borra.
    if (signal?.aborted) throw error;

    const abortado = error instanceof DOMException && error.name === "TimeoutError";
    throw new ErrorApi({
      mensaje: abortado
        ? "El servidor tardó demasiado en responder. Fijate que el sistema esté prendido."
        : "No pudimos conectarnos con el servidor. Revisá que el sistema esté corriendo.",
      codigo: abortado ? "TIMEOUT" : "SIN_CONEXION",
      estado: 0,
      cuerpo: error,
    });
  }

  // 204 y demas respuestas sin cuerpo.
  const bruto = await respuesta.text();
  let contenido: unknown = null;
  if (bruto !== "") {
    try {
      contenido = JSON.parse(bruto) as unknown;
    } catch {
      contenido = bruto;
    }
  }

  if (!respuesta.ok) {
    const error = errorDeRespuesta(respuesta.status, contenido);
    if (error.noAutenticado) avisarSesionPerdida();
    if (error.codigo === "PAUSA") avisarPausa();
    throw error;
  }

  // Un 200 con el sobre en `false` no existe hoy, pero si aparece es un error:
  // devolverlo como dato bueno dejaria a la pantalla mostrando `null`.
  if (esSobre(contenido) && contenido.ok === false) {
    throw errorDeRespuesta(respuesta.status, contenido);
  }

  return desenvolver<T>(contenido);
}

/** Atajos. Es lo que usan los hooks. */
export const api = {
  get: <T = unknown>(ruta: string, opciones?: Omit<OpcionesPedido, "cuerpo">) =>
    pedir<T>("GET", ruta, opciones),
  post: <T = unknown>(ruta: string, cuerpo?: unknown, opciones?: OpcionesPedido) =>
    pedir<T>("POST", ruta, { ...opciones, cuerpo }),
  patch: <T = unknown>(ruta: string, cuerpo?: unknown, opciones?: OpcionesPedido) =>
    pedir<T>("PATCH", ruta, { ...opciones, cuerpo }),
  put: <T = unknown>(ruta: string, cuerpo?: unknown, opciones?: OpcionesPedido) =>
    pedir<T>("PUT", ruta, { ...opciones, cuerpo }),
  delete: <T = unknown>(ruta: string, opciones?: OpcionesPedido) =>
    pedir<T>("DELETE", ruta, opciones),
  /** Sube un archivo. `subirCompra` no sirve: aquella informa progreso y usa XHR. */
  subir: <T = unknown>(
    metodo: Extract<Metodo, "POST" | "PUT">,
    ruta: string,
    formulario: FormData,
    opciones?: Omit<OpcionesPedido, "cuerpo" | "formulario">,
  ) => pedir<T>(metodo, ruta, { ...opciones, formulario }),
};
